import { useState, useCallback } from 'react';
import type React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';

import Modal from 'components/partials/modal/modal';
import { Input } from 'ui/input';
import Button from 'components/partials/button/button';

import Icons from 'assets/icons';
import toast from 'components/partials/toast/toast';

import type { ClientRecord } from 'hooks/api/use-clients';
import { useDeleteClient } from 'hooks/api/use-clients';

interface CurrentUserShape {
  data?: {
    addAndDeleteClients?: boolean;
    [key: string]: unknown;
  } | null;
  [key: string]: unknown;
}

interface ClientProfileDeleteBtnProps {
  client: ClientRecord;
  currentUser: CurrentUserShape;
}

export const ClientProfileDeleteBtn = ({
  client,
  currentUser,
}: ClientProfileDeleteBtnProps): React.JSX.Element | null => {
  const [showModal, setShowModal] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const navigate = useNavigate();

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      setInputValue(event.target.value);
    },
    [setInputValue]
  );

  const deleteClient = useDeleteClient();

  if (!currentUser?.data?.addAndDeleteClients) return null;

  const onConfirm = (): void => {
    deleteClient.mutate(
      { id: client.id, name: client.name, silent: true },
      {
        onSuccess: () => {
          toast.info({
            title: 'Client account has been deleted',
            message: `You've successfully deleted ${client.name} client`,
          });
          navigate('/admin/clients');
        },
        onError: (error: unknown) => {
          console.error(error);
        },
      }
    );
  };

  const bodyModal = (
    <>
      <p className="mt-2 mb-6 font-size-[18px] text-gray-700">
        By deleting this client from MARKETview{' '}
        <b className="font-semibold">all data</b> will be removed from all
        systems including any files the client has sent in the past. This action
        cannot be undone and is{' '}
        <b className="font-semibold">a permanent deletion</b> of data from
        databases and file storage.
      </p>

      <Input
        label="Please confirm this action by typing the client name"
        sx={{ mt: 1.5 }}
        placeholder="Client Name..."
        onChange={handleChange}
        data-testid="delete-confirmation-input"
      />
    </>
  );

  return (
    <>
      <Button
        className={classNames(
          'font-normal hover:bg-navy-50 !px-4 !justify-end !text-navy-500'
        )}
        variant="reserve-text"
        iconBefore={<Icons.Delete />}
        onClick={() => setShowModal(true)}
        data-testid="delete-client-button"
      >
        Delete client
      </Button>

      {showModal &&
        createPortal(
          <Modal
            headerText="Delete client"
            onConfirmCallback={() => {
              onConfirm();
              setShowModal(false);
            }}
            onConfirmButtonLabel="Submit"
            children={bodyModal}
            disabled={inputValue !== client?.name}
            disableEnterConfirm={inputValue !== client?.name}
            onCloseModal={() => {
              setInputValue('');
              setShowModal(false);
            }}
          />,
          document.body
        )}
    </>
  );
};
